import { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Layout from "../../components/Layout";
import api from "../../api";
import * as XLSX from "xlsx";

const PAY_META = {
  paid:    { label: "Paid",    bg: "#dcfce7", color: "#16a34a" },
  free:    { label: "Free",    bg: "#eff6ff", color: "#2563eb" },
  pending: { label: "Pending", bg: "#fff7ed", color: "#ea580c" },
  failed:  { label: "Failed",  bg: "#fef2f2", color: "#dc2626" },
};

const LEAD_TYPE_META = {
  speakership: { color: "#7C3AED", bg: "#F5F3FF", border: "#DDD6FE" },
  sponsorship: { color: "#059669", bg: "#ECFDF5", border: "#A7F3D0" },
  delegate:    { color: "#0369A1", bg: "#E0F2FE", border: "#BAE6FD" },
  nomination:  { color: "#D97706", bg: "#FEF3C7", border: "#FDE68A" },
};

function getLeadTypeMeta(type) {
  const key = (type || "").toLowerCase().trim();
  return LEAD_TYPE_META[key] || { color: "#64748B", bg: "#F1F5F9", border: "#E2E8F0" };
}

const INTERNAL_KEYS = new Set([
  "leadsource","leadtype","crmeventid","leadownerid","eventid","formid",
  "leadSource","leadType","crmEventId","leadOwnerId","eventId","formId",
  "zohoStatus","zohoLeadId","salutation","__v","_id",
]);

function getDelegateName(data) {
  if (!data) return "—";
  const first = data.firstName || data.firstname || data.FirstName || "";
  const last  = data.lastName  || data.lastname  || data.LastName  || "";
  if (first || last) return [first, last].filter(Boolean).join(" ");
  return getField(data, "fullname", "name", "nomineename", "applicantname") || "—";
}

const KNOWN_LEAD_TYPES = ["speakership", "sponsorship", "delegate", "nomination"];

function getLeadType(data) {
  if (!data) return null;
  // Primary: look for a field named lead_type / leadType / "Lead Type"
  const byName = Object.entries(data).find(([k]) =>
    k.toLowerCase().replace(/[\s_]/g, "") === "leadtype"
  );
  if (byName) return String(byName[1]).trim();
  // Fallback: scan all field VALUES for a known lead-type string
  // (covers Express Interest forms that store it under a different key)
  const byValue = Object.entries(data).find(([, v]) =>
    typeof v === "string" && KNOWN_LEAD_TYPES.includes(v.trim().toLowerCase())
  );
  return byValue ? String(byValue[1]).trim() : null;
}

function getField(data, ...keys) {
  const entry = Object.entries(data || {}).find(([k]) =>
    keys.some(t => k.toLowerCase().replace(/[\s_]/g, "") === t.toLowerCase())
  );
  return entry ? String(entry[1]) : "—";
}

function prettyKey(k) {
  return k.replace(/([A-Z])/g, " $1").replace(/^./, s => s.toUpperCase()).trim();
}

function PayBadge({ status }) {
  const m = PAY_META[status] || { label: status || "—", bg: "#f3f4f6", color: "#6b7280" };
  return (
    <span style={{ background: m.bg, color: m.color, fontWeight: 700, fontSize: 11, padding: "3px 8px", borderRadius: 8, textTransform: "uppercase", whiteSpace: "nowrap" }}>
      {m.label}
    </span>
  );
}

function DetailModal({ submission, onClose }) {
  const data   = submission.submissionData || {};
  const fields = Object.entries(data).filter(([k]) => {
    const norm = k.toLowerCase().replace(/[\s_]/g, "");
    return !INTERNAL_KEYS.has(k) && !INTERNAL_KEYS.has(norm);
  });
  const pm = PAY_META[submission.paymentStatus] || {};

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
      <div style={{ background: "#fff", borderRadius: 14, width: "100%", maxWidth: 580, maxHeight: "90vh", display: "flex", flexDirection: "column", boxShadow: "0 20px 60px rgba(0,0,0,0.2)" }}>

        {/* Header */}
        <div style={{ padding: "18px 22px", borderBottom: "1px solid #F1F5F9", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: "#0F172A" }}>Registration Details</h3>
            <p style={{ margin: "2px 0 0", fontSize: 12, color: "#94A3B8" }}>{submission.eventFormId?.formName}</p>
          </div>
          <button onClick={onClose} style={{ background: "#F1F5F9", border: "none", width: 30, height: 30, borderRadius: "50%", cursor: "pointer", fontSize: 15, color: "#64748B" }}>✕</button>
        </div>

        {/* Body */}
        <div style={{ overflowY: "auto", flex: 1, padding: "18px 22px" }}>

          {/* Payment summary bar */}
          <div style={{ marginBottom: 18, padding: "10px 14px", background: "#F8FAFC", borderRadius: 8, display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
            <PayBadge status={submission.paymentStatus} />
            {submission.finalAmount != null && submission.finalAmount > 0 && (
              <span style={{ fontSize: 13, color: "#0F172A", fontWeight: 600 }}>
                ₹{submission.finalAmount.toLocaleString()}
                {submission.discountAmount > 0 && <span style={{ fontSize: 11, color: "#10B981", marginLeft: 6 }}>(-₹{submission.discountAmount.toLocaleString()} discount)</span>}
              </span>
            )}
            {submission.couponCode && (
              <span style={{ fontSize: 12, color: "#64748B" }}>Coupon: <strong style={{ color: "#0F172A" }}>{submission.couponCode}</strong></span>
            )}
            <span style={{ marginLeft: "auto", fontSize: 11, color: "#94A3B8" }}>
              {new Date(submission.submittedAt).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}
            </span>
          </div>

          {/* Fields grid */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px 20px" }}>
            {fields.map(([key, val]) => {
              const isLong = typeof val === "string" && val.length > 60;
              const isUrl  = typeof val === "string" && val.startsWith("http");
              return (
                <div key={key} style={isLong ? { gridColumn: "1 / -1", borderBottom: "1px solid #F8FAFC", paddingBottom: 10 } : { borderBottom: "1px solid #F8FAFC", paddingBottom: 10 }}>
                  <p style={{ fontSize: 10.5, color: "#94A3B8", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", margin: "0 0 3px" }}>
                    {prettyKey(key)}
                  </p>
                  {isUrl ? (
                    <a href={val} target="_blank" rel="noreferrer" style={{ fontSize: 13, color: "#1D9AEC", fontWeight: 600, textDecoration: "none" }}>
                      View document ↗
                    </a>
                  ) : (
                    <p style={{ margin: 0, fontSize: 13, color: "#1E293B", lineHeight: 1.5, wordBreak: "break-word" }}>{String(val)}</p>
                  )}
                </div>
              );
            })}
          </div>

          {/* Group attendees */}
          {submission.attendees?.length > 0 && (
            <div style={{ marginTop: 18 }}>
              <p style={{ fontSize: 11, fontWeight: 700, color: "#94A3B8", textTransform: "uppercase", letterSpacing: "0.06em", margin: "0 0 10px" }}>
                Additional Attendees ({submission.attendees.length})
              </p>
              {submission.attendees.map((att, i) => (
                <div key={i} style={{ background: "#F8FAFC", borderRadius: 8, padding: "10px 14px", marginBottom: 8, fontSize: 13 }}>
                  {Object.entries(att)
                    .filter(([k]) => !INTERNAL_KEYS.has(k) && !INTERNAL_KEYS.has(k.toLowerCase().replace(/[\s_]/g,"")))
                    .map(([k, v]) => (
                      <span key={k} style={{ marginRight: 16, display: "inline-block" }}>
                        <strong style={{ color: "#94A3B8", fontSize: 11 }}>{prettyKey(k)}: </strong>
                        <span style={{ color: "#374151" }}>{String(v)}</span>
                      </span>
                    ))}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function AdminDelegatesSubmissionsPage() {
  const { eventId } = useParams();
  const navigate    = useNavigate();
  const [data,       setData]       = useState(null);
  const [formFilter,     setFormFilter]     = useState("all");
  const [payFilter,      setPayFilter]      = useState("all");
  const [leadTypeFilter, setLeadTypeFilter] = useState("all");
  const [search,         setSearch]         = useState("");
  const [detail,         setDetail]         = useState(null);

  useEffect(() => {
    api.get(`/jury/delegates/event/${eventId}`)
      .then(res => setData(res.data.data))
      .catch(() => setData({ forms: [], submissions: [] }));
  }, [eventId]);

  const submissions = data?.submissions || [];
  const forms       = data?.forms       || [];

  const leadTypes = useMemo(() => {
    const types = new Set();
    submissions.forEach(s => {
      const lt = getLeadType(s.submissionData);
      if (lt) types.add(lt);
    });
    return [...types].sort();
  }, [submissions]);

  const displayed = useMemo(() => {
    let list = submissions;
    if (formFilter     !== "all") list = list.filter(s => (s.eventFormId?._id || s.eventFormId) === formFilter);
    if (payFilter      !== "all") list = list.filter(s => s.paymentStatus === payFilter);
    if (leadTypeFilter !== "all") list = list.filter(s => (getLeadType(s.submissionData) || "").toLowerCase() === leadTypeFilter.toLowerCase());
    const q = search.trim().toLowerCase();
    if (q) list = list.filter(s => JSON.stringify(s.submissionData || {}).toLowerCase().includes(q));
    return list;
  }, [submissions, formFilter, payFilter, leadTypeFilter, search]);

  const stats = useMemo(() => ({
    total:   submissions.length,
    paid:    submissions.filter(s => s.paymentStatus === "paid").length,
    free:    submissions.filter(s => s.paymentStatus === "free").length,
    pending: submissions.filter(s => s.paymentStatus === "pending").length,
    failed:  submissions.filter(s => s.paymentStatus === "failed").length,
  }), [submissions]);

  const exportExcel = () => {
    const rows = displayed.map(s => {
      const sd  = s.submissionData || {};
      const row = {
        Form:              s.eventFormId?.formName || "",
        "Lead Type":       getLeadType(sd) || "",
        "Payment Status":  s.paymentStatus || "",
        "Amount (₹)":     s.finalAmount   || 0,
        "Coupon":          s.couponCode    || "",
        "Submitted At":    new Date(s.submittedAt).toLocaleString("en-IN"),
      };
      Object.entries(sd).forEach(([k, v]) => {
        const norm = k.toLowerCase().replace(/[\s_]/g, "");
        if (!INTERNAL_KEYS.has(k) && !INTERNAL_KEYS.has(norm)) row[prettyKey(k)] = String(v);
      });
      return row;
    });
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Delegates");
    XLSX.writeFile(wb, `delegates-${eventId}.xlsx`);
  };

  const formSubmissionCount = (fid) => submissions.filter(s => (s.eventFormId?._id || s.eventFormId) === fid).length;

  return (
    <Layout title="Delegate Registrations">
      {detail && <DetailModal submission={detail} onClose={() => setDetail(null)} />}

      <button
        onClick={() => navigate("/admin/delegates")}
        style={{ marginBottom: 18, background: "#fff", border: "1px solid #E2E8F0", borderRadius: 8, padding: "6px 14px", cursor: "pointer", fontSize: 13, fontWeight: 600, color: "#475569", display: "inline-flex", alignItems: "center", gap: 6, boxShadow: "0 1px 3px rgba(0,0,0,0.06)" }}
      >
        ← Back to Events
      </button>

      {/* Stats row */}
      {data && (
        <div style={{ display: "flex", gap: 10, marginBottom: 22, flexWrap: "wrap" }}>
          {[
            { label: "Total",   val: stats.total,   color: "#374151", bg: "#F8FAFC",  border: "#E2E8F0" },
            { label: "Paid",    val: stats.paid,    color: "#16a34a", bg: "#dcfce7",  border: "#bbf7d0" },
            { label: "Free",    val: stats.free,    color: "#2563eb", bg: "#eff6ff",  border: "#bfdbfe" },
            { label: "Pending", val: stats.pending, color: "#ea580c", bg: "#fff7ed",  border: "#fed7aa" },
            { label: "Failed",  val: stats.failed,  color: "#dc2626", bg: "#fef2f2",  border: "#fecaca" },
          ].filter(s => s.val > 0 || s.label === "Total").map(s => (
            <div key={s.label} style={{ background: s.bg, border: `1px solid ${s.border}`, borderRadius: 8, padding: "8px 18px", textAlign: "center", minWidth: 64 }}>
              <div style={{ fontSize: 22, fontWeight: 800, color: s.color, lineHeight: 1 }}>{s.val}</div>
              <div style={{ fontSize: 11, color: s.color, fontWeight: 600, marginTop: 2 }}>{s.label}</div>
            </div>
          ))}
        </div>
      )}

      {/* Form tabs */}
      {forms.length > 1 && (
        <div style={{ display: "flex", gap: 6, marginBottom: 16, flexWrap: "wrap" }}>
          <button
            onClick={() => setFormFilter("all")}
            style={{ padding: "6px 16px", borderRadius: 20, border: "none", cursor: "pointer", fontWeight: 600, fontSize: 12, background: formFilter === "all" ? "#6366F1" : "#e8e8e8", color: formFilter === "all" ? "#fff" : "#555" }}
          >
            All Forms
            <span style={{ marginLeft: 5, background: formFilter === "all" ? "rgba(255,255,255,0.25)" : "#d1d5db", color: formFilter === "all" ? "#fff" : "#6b7280", borderRadius: 10, padding: "1px 6px", fontSize: 10 }}>
              {submissions.length}
            </span>
          </button>
          {forms.map(f => (
            <button
              key={f._id}
              onClick={() => setFormFilter(f._id)}
              style={{ padding: "6px 16px", borderRadius: 20, border: "none", cursor: "pointer", fontWeight: 600, fontSize: 12, background: formFilter === f._id ? "#6366F1" : "#e8e8e8", color: formFilter === f._id ? "#fff" : "#555" }}
            >
              {f.formName}
              <span style={{ marginLeft: 5, background: formFilter === f._id ? "rgba(255,255,255,0.25)" : "#d1d5db", color: formFilter === f._id ? "#fff" : "#6b7280", borderRadius: 10, padding: "1px 6px", fontSize: 10 }}>
                {formSubmissionCount(f._id)}
              </span>
            </button>
          ))}
        </div>
      )}

      {/* Lead type tabs */}
      {leadTypes.length > 0 && (
        <div style={{ display: "flex", gap: 6, marginBottom: 16, flexWrap: "wrap", alignItems: "center" }}>
          <span style={{ fontSize: 11, fontWeight: 700, color: "#94A3B8", textTransform: "uppercase", letterSpacing: "0.06em", marginRight: 4 }}>Type:</span>
          <button
            onClick={() => setLeadTypeFilter("all")}
            style={{ padding: "5px 14px", borderRadius: 20, border: `1.5px solid ${leadTypeFilter === "all" ? "#374151" : "#E2E8F0"}`, cursor: "pointer", fontWeight: 600, fontSize: 12, background: leadTypeFilter === "all" ? "#374151" : "#fff", color: leadTypeFilter === "all" ? "#fff" : "#555" }}
          >
            All
            <span style={{ marginLeft: 5, background: leadTypeFilter === "all" ? "rgba(255,255,255,0.25)" : "#E2E8F0", color: leadTypeFilter === "all" ? "#fff" : "#6b7280", borderRadius: 10, padding: "1px 6px", fontSize: 10 }}>
              {submissions.length}
            </span>
          </button>
          {leadTypes.map(lt => {
            const meta  = getLeadTypeMeta(lt);
            const count = submissions.filter(s => (getLeadType(s.submissionData) || "").toLowerCase() === lt.toLowerCase()).length;
            const active = leadTypeFilter.toLowerCase() === lt.toLowerCase();
            return (
              <button
                key={lt}
                onClick={() => setLeadTypeFilter(lt)}
                style={{ padding: "5px 14px", borderRadius: 20, border: `1.5px solid ${active ? meta.color : meta.border}`, cursor: "pointer", fontWeight: 600, fontSize: 12, background: active ? meta.color : meta.bg, color: active ? "#fff" : meta.color }}
              >
                {lt}
                <span style={{ marginLeft: 5, background: active ? "rgba(255,255,255,0.25)" : meta.border, color: active ? "#fff" : meta.color, borderRadius: 10, padding: "1px 6px", fontSize: 10 }}>
                  {count}
                </span>
              </button>
            );
          })}
        </div>
      )}

      {/* Toolbar */}
      <div style={{ display: "flex", gap: 10, marginBottom: 14, flexWrap: "wrap", alignItems: "center" }}>
        <div style={{ flex: 1, minWidth: 200, position: "relative" }}>
          <span style={{ position: "absolute", left: 11, top: "50%", transform: "translateY(-50%)", fontSize: 14, color: "#9ca3af" }}>🔍</span>
          <input
            type="text" placeholder="Search by name, email, phone…" value={search}
            onChange={e => setSearch(e.target.value)}
            style={{ width: "100%", padding: "9px 14px 9px 34px", border: "1px solid #e5e7eb", borderRadius: 8, fontSize: 13, boxSizing: "border-box", outline: "none", background: "#f9fafb", fontFamily: "inherit" }}
          />
          {search && (
            <button onClick={() => setSearch("")} style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: "#9ca3af", fontSize: 15 }}>✕</button>
          )}
        </div>
        <select value={payFilter} onChange={e => setPayFilter(e.target.value)} style={{ padding: "9px 12px", border: "1px solid #e5e7eb", borderRadius: 8, fontSize: 13, outline: "none", background: "#f9fafb", cursor: "pointer" }}>
          <option value="all">All Payments</option>
          {Object.entries(PAY_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
        <button onClick={exportExcel} style={{ background: "#f0fdf4", color: "#16a34a", border: "1px solid #bbf7d0", padding: "9px 16px", borderRadius: 8, fontWeight: 600, fontSize: 13, cursor: "pointer", whiteSpace: "nowrap" }}>
          ⬇ Export Excel
        </button>
      </div>

      <p style={{ fontSize: 12, color: "#9ca3af", marginBottom: 12 }}>
        Showing <strong style={{ color: "#374151" }}>{displayed.length}</strong> of <strong style={{ color: "#374151" }}>{submissions.length}</strong> registrations
      </p>

      {/* Table */}
      {!data ? (
        <div style={{ background: "#fff", borderRadius: 10, padding: 32, boxShadow: "0 1px 4px rgba(0,0,0,0.06)" }}>
          <div className="skeleton" style={{ height: 14, width: "60%", marginBottom: 10 }} />
          <div className="skeleton" style={{ height: 10, width: "40%" }} />
        </div>
      ) : displayed.length === 0 ? (
        <div style={{ background: "#fff", borderRadius: 10, padding: "48px 24px", textAlign: "center", boxShadow: "0 1px 4px rgba(0,0,0,0.06)" }}>
          <div style={{ fontSize: 36, marginBottom: 10 }}>📋</div>
          <p style={{ fontWeight: 600, color: "#374151", margin: "0 0 4px" }}>No registrations found</p>
          <p style={{ color: "#9ca3af", fontSize: 13, margin: 0 }}>
            {submissions.length === 0 ? "No delegates have registered for this event yet." : "Try adjusting your search or filters."}
          </p>
        </div>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", background: "#fff", borderRadius: 10, overflow: "hidden", boxShadow: "0 2px 8px rgba(0,0,0,0.05)" }}>
            <thead>
              <tr style={{ background: "#f8f9fa" }}>
                {["#", "Name", "Email", "Phone", "Organisation", leadTypes.length > 0 && "Lead Type", forms.length > 1 && "Form", "Payment", "Amount", "Date", ""].filter(Boolean).map(h => (
                  <th key={h} style={{ padding: "11px 14px", textAlign: "left", fontSize: 11.5, fontWeight: 700, color: "#555", textTransform: "uppercase", letterSpacing: "0.04em", whiteSpace: "nowrap" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {displayed.map((s, idx) => {
                const sd   = s.submissionData || {};
                const name = getDelegateName(sd);
                const email = getField(sd, "email", "emailid", "emailaddress");
                const phone = getField(sd, "mobile", "phone", "mobilenumber", "phonenumber", "contact");
                const org   = getField(sd, "organisation", "organization", "company", "hospital", "institutename", "clinic");

                return (
                  <tr key={s._id} style={{ borderTop: idx > 0 ? "1px solid #f0f0f0" : "none" }}>
                    <td style={td}><span style={{ fontSize: 11, color: "#94A3B8", fontWeight: 600 }}>{idx + 1}</span></td>
                    <td style={td}>
                      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                        <span style={{ fontWeight: 600, color: "#0F172A", fontSize: 13.5 }}>{name}</span>
                        {s.attendees?.length > 0 && (
                          <span title={`Group of ${s.attendees.length + 1}`} style={{ fontSize: 10, fontWeight: 700, background: "#EEF2FF", color: "#4F46E5", padding: "2px 6px", borderRadius: 6, whiteSpace: "nowrap" }}>
                            +{s.attendees.length}
                          </span>
                        )}
                      </div>
                    </td>
                    <td style={{ ...td, color: "#64748B" }}>{email}</td>
                    <td style={{ ...td, color: "#64748B" }}>{phone}</td>
                    <td style={{ ...td, color: "#64748B", maxWidth: 160, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{org}</td>
                    {leadTypes.length > 0 && (() => {
                      const lt   = getLeadType(sd);
                      const meta = getLeadTypeMeta(lt);
                      return (
                        <td style={td}>
                          {lt ? (
                            <span style={{ background: meta.bg, color: meta.color, border: `1px solid ${meta.border}`, fontWeight: 700, fontSize: 11, padding: "3px 9px", borderRadius: 8, whiteSpace: "nowrap" }}>
                              {lt}
                            </span>
                          ) : <span style={{ color: "#CBD5E1" }}>—</span>}
                        </td>
                      );
                    })()}
                    {forms.length > 1 && (
                      <td style={td}>
                        <span style={{ background: "#EEF2FF", color: "#4F46E5", fontWeight: 600, fontSize: 11.5, padding: "3px 8px", borderRadius: 6, whiteSpace: "nowrap" }}>
                          {s.eventFormId?.formName || "—"}
                        </span>
                      </td>
                    )}
                    <td style={td}><PayBadge status={s.paymentStatus} /></td>
                    <td style={{ ...td, fontWeight: 700, color: "#1D9AEC" }}>
                      {s.finalAmount != null && s.finalAmount > 0 ? `₹${s.finalAmount.toLocaleString()}` : "—"}
                    </td>
                    <td style={{ ...td, color: "#94A3B8", fontSize: 12, whiteSpace: "nowrap" }}>
                      {new Date(s.submittedAt).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "2-digit" })}
                    </td>
                    <td style={td}>
                      <button
                        onClick={() => setDetail(s)}
                        style={{ background: "#F8FAFC", border: "1px solid #E2E8F0", color: "#475569", padding: "5px 12px", borderRadius: 6, cursor: "pointer", fontSize: 12, fontWeight: 600 }}
                      >
                        View
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </Layout>
  );
}

const td = { padding: "11px 14px", fontSize: 13, color: "#333", verticalAlign: "middle" };
